import { inject, Injectable } from '@angular/core';
import { LoggingService } from '@nx-vigil/core/web/logging/data-access';
import { getFirstErrorFromResponse } from '@nx-vigil/core/web/shared/util';
import { Observable, Subject } from 'rxjs';
import { Alert, ALERT_TYPE, AlertLogLevel, AlertNotifyOptions } from './alert';

const defaultAlert: Alert = {
  message: '',
  type: ALERT_TYPE.Info,
  duration: 5000,
};

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private readonly _alert$ = new Subject<Alert>();
  private readonly _logger = inject(LoggingService);

  public get alert$(): Observable<Alert> {
    return this._alert$.asObservable();
  }

  /**
   * Emits a success alert. Logging is suppressed by default.
   * Pass `{ log: 'debug' }` to enable debug-level logging.
   */
  public success(message: string, options?: AlertNotifyOptions): void {
    this.log('off', message, options);
    this.alert({ type: ALERT_TYPE.Success, message });
  }

  public error(
    message: string,
    error?: Error,
    options?: AlertNotifyOptions,
  ): string {
    let resolvedMsg = message;

    if (error?.message) {
      const innerMsg = getFirstErrorFromResponse(error);
      const logException = innerMsg ?? error.message;
      if (innerMsg) resolvedMsg = innerMsg;
      this.log(
        'error',
        '{message}: {exception}',
        options,
        resolvedMsg,
        logException,
      );
    } else {
      this.log('error', resolvedMsg, options);
    }

    this.alert({ type: ALERT_TYPE.Error, message: resolvedMsg });
    return resolvedMsg;
  }

  public info(message: string, options?: AlertNotifyOptions): void {
    this.log('info', message, options);
    this.alert({ type: ALERT_TYPE.Info, message });
  }

  public alert(alert: Alert): void {
    if (!alert.message) return;
    this._alert$.next({ ...defaultAlert, ...alert });
  }

  private log(
    defaultLevel: AlertLogLevel,
    message: string,
    options?: AlertNotifyOptions,
    ...additional: unknown[]
  ): void {
    const level = options?.log ?? defaultLevel;
    if (level === 'off') return;
    this._logger[level](message, ...additional);
  }
}
